import type { ReactNode } from "react";
import type { ListWatchesStatus200 } from "@site-spy/api-client";
import {
  columnFacetingFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createSortedRowModel,
  filterFn_equalsString,
  globalFilteringFeature,
  rowSortingFeature,
  tableFeatures,
  type Column,
  type FilterFn,
} from "@tanstack/react-table";
import styles from "./watches.module.css";

// ── TanStack Table v9 feature registry ──
// v9 no longer bundles features automatically: every feature the watches table
// uses has to be registered here, and each row-model slot must come after the
// feature it depends on. `filterFn_equalsString` is registered by name because
// the status column still references it as the string `"equalsString"`.
export const watchTableFeatures = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  columnFacetingFeature,
  // required for row.getVisibleCells() in the table body
  columnVisibilityFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  filterFns: { equalsString: filterFn_equalsString },
});

export type WatchTableFeatures = typeof watchTableFeatures;

// ── Types ──

export interface WatchItem {
  uuid: string;
  url: string;
  /** Raw stored title. Empty unless someone typed one; the rename editor writes it. */
  title: string;
  /**
   * What to call this watch on screen, resolved by the backend so the row and
   * the email subject cannot disagree. Falls back to the stored title, so an
   * older server that does not send it changes nothing.
   */
  label: string;
  last_checked: number;
  last_changed: number;
  last_viewed: number;
  has_error?: boolean;
  last_error?: string;
  checking_now?: boolean;
  queued?: boolean;
  history_n: number;
  tags: string[];
  processor?: string;
  screenshot_enabled?: boolean;
  check_count?: number;
  filter_count?: number;
  ai_tuning_started_at?: number;
  tune_count_today?: number;
  tune_limit?: number;
  notification_alert_count?: number;
  notifications_per_day?: number;
  noisy_notifications_per_day?: number;
  filters_source?: string;
  track_what?: string;
  trigger_text?: string[];
  text_should_not_be_present?: string[];
  paused?: boolean;
  paused_by?: "plan_limit" | "reliability" | null;
  paused_at?: number;
  health?: WatchHealth;
  /** Set while the owner's monthly checks are used up; resumes by itself */
  check_budget_hold?: { resumes_at: number } | null;
}

export type WatchHealthState =
  | "healthy"
  | "never_checked"
  | "recovering"
  | "blocked"
  | "rate_limited"
  | "authentication_required"
  | "target_unavailable"
  | "empty_content"
  | "filter_broken"
  | "notification_failure"
  | "auto_paused"
  | "delivery_blocked";

export type WatchHealthAction =
  | "retry_fetch"
  | "resume"
  | "update_url"
  | "provide_auth"
  | "reconfigure_filter"
  | "switch_monitor_type"
  | "reduce_frequency"
  | "fix_notifications";

export interface WatchHealth {
  state: WatchHealthState;
  reason: string;
  action: WatchHealthAction | null;
  /** One concrete move for the owner. Empty while the watch is healthy. */
  next_step?: string;
  /**
   * Whether picking an element on the stored page repairs this watch. `null`
   * means the backend has no verdict for this failure — say nothing, rather
   * than guessing in either direction.
   */
  pointing_repairs?: boolean | null;
  since_ts: number;
  category?: string | null;
  code?: string | null;
  consecutive_failures?: number;
  next_retry_at?: number;
  recovery_stage?: number;
  auto_paused?: boolean;
}

export type WatchStatus =
  | "paused"
  | "error"
  | "filter_broken"
  | "blocked"
  | "rate_limited"
  | "authentication_required"
  | "empty_content"
  | "target_unavailable"
  | "notification_failure"
  | "delivery_blocked"
  | "recovering"
  | "changed"
  | "viewed"
  | "pending"
  | "queued"
  | "no_changes";

// ── Helpers ──

export function getWatchStatus(watch: WatchItem): WatchStatus {
  // Ranked first so the filter agrees with the badge. A paused watch used to
  // classify as "error" off its stale health state, which put watches that are
  // merely switched off into the same bucket as ones actively failing.
  if (watch.paused) return "paused";
  // The backend already worked out why a watch is unhealthy; collapsing all of
  // it into "Error" threw that away and left the owner with nothing to act on.
  // A site refusing us, a login wall and a broken filter need three different
  // responses from three different people.
  const state = watch.health?.state;
  if (state === "filter_broken") return "filter_broken";
  if (state === "blocked") return "blocked";
  if (state === "rate_limited") return "rate_limited";
  if (state === "authentication_required") return "authentication_required";
  if (state === "empty_content") return "empty_content";
  if (state === "target_unavailable") return "target_unavailable";
  if (state === "notification_failure") return "notification_failure";
  if (state === "delivery_blocked") return "delivery_blocked";
  if (state === "recovering") return "recovering";
  if (state && state !== "healthy" && state !== "never_checked") return "error";
  if (watch.has_error) return "error";
  // Below the health states on purpose: waiting in the queue is transient and
  // must not hide a problem the owner has to act on. Above "no changes"
  // because hiding it entirely left the badge reading "No changes" next to a
  // recheck button that was spinning and disabled off that very field. One
  // page per site is fetched at a time, so waiting is common and can last
  // minutes.
  if (watch.queued && !watch.checking_now) return "queued";
  if (!watch.history_n) return "pending";
  if (watch.last_changed > 0 && watch.last_changed > watch.last_viewed)
    return "changed";
  if (watch.last_changed > 0) return "viewed";
  return "no_changes";
}

export const STATUS_LABELS: Record<WatchStatus, string> = {
  paused: "Paused",
  changed: "Changed",
  viewed: "Viewed",
  pending: "Pending",
  queued: "Queued",
  error: "Error",
  filter_broken: "Filter broken",
  blocked: "Blocked by site",
  rate_limited: "Checking too often",
  authentication_required: "Login required",
  empty_content: "No content found",
  target_unavailable: "Page unavailable",
  notification_failure: "Notifications failing",
  delivery_blocked: "Delivery blocked",
  recovering: "Retrying",
  no_changes: "No changes",
};

export const TIME_RANGE_LABELS: Record<string, string> = {
  today: "Today",
  week: "This week",
  month: "This month",
  never: "Never changed",
};

export function transformWatchesResponse(
  data: ListWatchesStatus200 | undefined,
): WatchItem[] {
  if (!data || typeof data !== "object") return [];
  return Object.entries(data).map(([uuid, watch]) => ({
    uuid,
    url: watch.url || "",
    title: watch.title || "",
    label:
      ((watch as Record<string, unknown>).label as string | undefined) ||
      watch.title ||
      "",
    last_checked: watch.last_checked || 0,
    last_changed: watch.last_changed || 0,
    last_viewed:
      ((watch as Record<string, unknown>).last_viewed as number) || 0,
    history_n: watch.history_n || 0,
    has_error: !!watch.last_error,
    last_error:
      typeof watch.last_error === "string" ? watch.last_error : undefined,
    checking_now: (watch as Record<string, unknown>).checking_now as
      boolean | undefined,
    queued: (watch as Record<string, unknown>).queued as boolean | undefined,
    tags: watch.tags || (watch.tag ? [watch.tag] : []),
    processor: (watch as Record<string, unknown>).processor as
      string | undefined,
    screenshot_enabled: !!(watch as Record<string, unknown>).screenshot_enabled,
    filter_count: (watch as Record<string, unknown>).filter_count as
      number | undefined,
    ai_tuning_started_at: (watch as Record<string, unknown>)
      .ai_tuning_started_at as number | undefined,
    tune_count_today: (watch as Record<string, unknown>).tune_count_today as
      number | undefined,
    tune_limit: (watch as Record<string, unknown>).tune_limit as
      number | undefined,
    check_count: (watch as Record<string, unknown>).check_count as
      number | undefined,
    notification_alert_count: (watch as Record<string, unknown>)
      .notification_alert_count as number | undefined,
    notifications_per_day: (watch as Record<string, unknown>)
      .notifications_per_day as number | undefined,
    noisy_notifications_per_day: (watch as Record<string, unknown>)
      .noisy_notifications_per_day as number | undefined,
    filters_source: (watch as Record<string, unknown>).filters_source as
      string | undefined,
    track_what: (watch as Record<string, unknown>).track_what as
      string | undefined,
    trigger_text: (watch as Record<string, unknown>).trigger_text as
      string[] | undefined,
    text_should_not_be_present: (watch as Record<string, unknown>)
      .text_should_not_be_present as string[] | undefined,
    health: (watch as Record<string, unknown>).health as
      WatchHealth | undefined,
    paused: Boolean((watch as Record<string, unknown>).paused),
    paused_by: (watch as Record<string, unknown>).paused_by as
      "plan_limit" | "reliability" | null | undefined,
    paused_at:
      ((watch as Record<string, unknown>).paused_at as number | undefined) ?? 0,
    check_budget_hold: (watch as Record<string, unknown>).check_budget_hold as
      { resumes_at: number } | null | undefined,
  }));
}

// A tuning run that died without clearing its marker must not leave the badge
// stuck on forever, so the marker expires on read. The server applies the same
// window before it reports the field, so this is a second line of defence for
// clients reading a cached payload rather than the only rule in play.
const TUNING_STALE_AFTER_SECONDS = 10 * 60;

export function isTuningFilters(w: WatchItem): boolean {
  const startedAt = w.ai_tuning_started_at ?? 0;
  if (!startedAt) return false;
  return Date.now() / 1000 - startedAt < TUNING_STALE_AFTER_SECONDS;
}

/** Tunes left in this watch's daily budget. `null` when the server didn't say. */
export function tunesRemaining(w: WatchItem): number | null {
  const limit = w.tune_limit;
  if (limit === undefined || limit <= 0) return null;
  return Math.max(0, limit - (w.tune_count_today ?? 0));
}

export function isTuneBudgetSpent(w: WatchItem): boolean {
  return tunesRemaining(w) === 0;
}

export function isPlanPaused(w: WatchItem): boolean {
  return Boolean(w.paused) && w.paused_by === "plan_limit";
}

/** Whether anything is still checking this page.
 *
 * `getWatchStatus` already ranks `paused` above every health state, for the
 * reason stated there: a watch that is merely switched off is not failing, and
 * it is not working either. Anything counting how much of an account is live
 * has to ask the same question, or the landing page's total and the row's
 * badge end up disagreeing about whether a watch is on.
 *
 * An absent `paused` reads as running, matching `getWatchStatus`'s own
 * `if (watch.paused)`. The field is always sent; agreeing with the badge when
 * it somehow is not matters more than guessing.
 */
export function isWatchRunning(watch: { paused?: boolean }): boolean {
  return !watch.paused;
}

export interface WatchRunState {
  total: number;
  running: number;
  paused: number;
  /**
   * Switched off by us rather than by the owner — `plan_limit` or
   * `reliability`. Worth separating because a watch the owner paused needs no
   * explanation and one we paused does.
   */
  pausedAutomatically: number;
}

/** How much of an account is actually being checked. */
export function summariseWatchRunState(
  watches: readonly { paused?: boolean; paused_by?: string | null }[],
): WatchRunState {
  let running = 0;
  let pausedAutomatically = 0;
  for (const watch of watches) {
    if (isWatchRunning(watch)) running += 1;
    else if (watch.paused_by) pausedAutomatically += 1;
  }
  return {
    total: watches.length,
    running,
    paused: watches.length - running,
    pausedAutomatically,
  };
}

/** What to tell an owner whose every watch is switched off.
 *
 * `null` while anything is still being checked, so the caller cannot show it
 * beside a reassurance that is true. Pure so the wording is testable: the two
 * sentences it replaced were shown to owners with nothing running, and the
 * wording is the whole fix.
 *
 * It never says *why* a particular watch is off. The row already carries the
 * per-code reason and remedy; repeating a guess here is how the two come to
 * disagree — and "repeated problems with the page" is false for a watch parked
 * for exceeding a plan.
 */
export function nothingRunningNotice(
  runState: WatchRunState,
): { state: string; cause: string; action: string } | null {
  if (runState.total === 0 || runState.running > 0) return null;
  const { total, pausedAutomatically } = runState;
  const one = total === 1;
  const them = one ? "it" : "them";
  const subject = one ? "Your watch is" : `All ${total} of your watches are`;
  let cause: string;
  if (pausedAutomatically === 0) {
    cause = `You paused ${them}.`;
  } else if (pausedAutomatically === total) {
    cause = `We switched ${them} off.`;
  } else {
    cause = `${pausedAutomatically} of ${them} ${
      pausedAutomatically === 1 ? "was" : "were"
    } switched off automatically.`;
  }
  return {
    state: `Nothing is being checked right now. ${subject} switched off.`,
    cause,
    action: `Open the list to see why and turn ${them} back on`,
  };
}

/** Watches where the missing intent is actually costing the owner something.
 *
 * Most watches with no stated intent behave perfectly well and asking those
 * anything would be noise. The ones worth asking are mailing their owner
 * several times a day, or reporting nothing at all for hundreds of checks with
 * no trigger to explain it. Both are what "this watch is pointed at the whole
 * page" looks like from outside, and the answer to both is the picker.
 *
 * Loudness is notifications per DAY, and the mark it is measured against comes
 * from the server. This used to be notifications over CHECKS, above 0.9, and
 * that rule reached nobody: the check interval is set by the owner's plan
 * rather than by the page, so the same twenty mails a day score 0.83 on an
 * hourly watch and 0.014 on a one-minute one. Measured across every watch on
 * the platform with at least twenty checks, the highest ratio ever recorded
 * was 0.825 -- the bar sat above every value the product had ever produced,
 * and the loudest watch in production (a fifth of all notifications sent, no
 * positive selector, no stated intent) scored 0.594 and was never asked.
 */
export function needsIntentToImprove(
  watch: WatchItem,
): null | "noisy" | "silent" {
  if ((watch.track_what ?? "").trim()) return null;
  if (watch.paused) return null;
  const checks = watch.check_count ?? 0;
  if (checks < 20) return null;

  // No mark means an older server that does not send one. Stay quiet rather
  // than guess a number here -- a copy of the threshold in this file is the
  // second definition that caused the defect above.
  const mark = watch.noisy_notifications_per_day;
  const perDay = watch.notifications_per_day ?? 0;
  if (typeof mark === "number" && mark > 0 && perDay >= mark) return "noisy";

  const notifs = watch.notification_alert_count ?? 0;
  const gated =
    (watch.trigger_text?.filter(Boolean).length ?? 0) > 0 ||
    (watch.text_should_not_be_present?.filter(Boolean).length ?? 0) > 0;
  if (checks > 200 && notifs === 0 && !gated) return "silent";
  return null;
}

export function formatTimeAgo(timestamp: number): string {
  if (!timestamp) return "Never";
  const seconds = Math.floor(Date.now() / 1000 - timestamp);
  // A future timestamp (e.g. health.next_retry_at) yields a negative delta,
  // which would otherwise fall into the "< 60" branch and read "Just now".
  if (seconds < 0) return formatTimeUntil(timestamp);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function getFaviconUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;
  } catch {
    return "";
  }
}

export function cleanUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    let clean = urlObj.hostname.replace(/^www\./, "");
    if (urlObj.pathname && urlObj.pathname !== "/") {
      clean += urlObj.pathname;
    }
    return clean;
  } catch {
    return url;
  }
}

export function redactUrlForDisplay(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.username = "";
    parsed.password = "";
    parsed.hash = "";
    for (const key of Array.from(parsed.searchParams.keys())) {
      parsed.searchParams.set(key, "[redacted]");
    }
    return parsed.toString();
  } catch {
    return url.replace(/([?&][^=&#\s]+)=([^&#\s]*)/g, "$1=[redacted]");
  }
}

// ── TanStack Table filter functions ──

export const timeRangeFilterFn: FilterFn<WatchTableFeatures, WatchItem> = (
  row,
  columnId,
  filterValue,
) => {
  if (!filterValue) return true;
  const timestamp = row.getValue<number>(columnId);
  const now = Math.floor(Date.now() / 1000);
  switch (filterValue) {
    case "today":
      return timestamp > now - 86400;
    case "week":
      return timestamp > now - 604800;
    case "month":
      return timestamp > now - 2592000;
    case "never":
      return !timestamp;
    default:
      return true;
  }
};

// ── Sortable header component ──

export function SortableHeader({
  column,
  children,
}: {
  column: Column<WatchTableFeatures, WatchItem, unknown>;
  children: ReactNode;
}) {
  return (
    <button
      className={styles.sortableHeader}
      onClick={() => column.toggleSorting()}
    >
      {children}
      {column.getIsSorted() && (
        <span className={styles.sortIndicator}>
          {column.getIsSorted() === "asc" ? "\u2191" : "\u2193"}
        </span>
      )}
    </button>
  );
}

/**
 * Drop blank and whitespace-only lines from a filter textarea's value.
 *
 * The textareas keep empty lines in state while editing — filtering them in
 * `onChange` would strip the newline the user just typed and make a second
 * line impossible to enter — so they are cleaned here on save instead.
 */
export function cleanFilterLines(lines: string[]): string[] {
  return lines.map((line) => line.trim()).filter((line) => line.length > 0);
}

/** Forward-looking counterpart to formatTimeAgo, for scheduled/future times. */
export function formatTimeUntil(timestamp: number): string {
  if (!timestamp) return "Never";
  const seconds = Math.floor(timestamp - Date.now() / 1000);
  if (seconds <= 0) return "now";
  if (seconds < 60) return "in <1m";
  if (seconds < 3600) return `in ${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `in ${Math.floor(seconds / 3600)}h`;
  return `in ${Math.floor(seconds / 86400)}d`;
}
